// src/services/weatherService.js
// Weather data from National Weather Service API (api.weather.gov)

const NWS_BASE = 'https://api.weather.gov';
const USER_AGENT = 'MrPatchAsphaltCRM/1.0 (contact@mrpatchasphalt.com)';
const CACHE_TTL = 30 * 60 * 1000;           // 30 minutes for weather data
const POINTS_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours for grid points

// ─── Cache helpers (localStorage) ────────────────────────────────────────────

const getCacheKey = (lat, lng) => {
  const latR = parseFloat(lat).toFixed(2);
  const lngR = parseFloat(lng).toFixed(2);
  return `weather_cache_${latR}_${lngR}`;
};

const getPointsCacheKey = (lat, lng) => {
  const latR = parseFloat(lat).toFixed(2);
  const lngR = parseFloat(lng).toFixed(2);
  return `weather_points_${latR}_${lngR}`;
};

const getHourlyCacheKey = (lat, lng) => {
  const latR = parseFloat(lat).toFixed(2);
  const lngR = parseFloat(lng).toFixed(2);
  return `weather_hourly_${latR}_${lngR}`;
};

const getCachedData = (key, ttl = CACHE_TTL) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (Date.now() - cached.timestamp < ttl) return cached.data;
    return null;
  } catch {
    localStorage.removeItem(key);
    return null;
  }
};

const setCachedData = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify({ timestamp: Date.now(), data }));
  } catch {
    // localStorage full — clear old weather caches
    Object.keys(localStorage)
      .filter((k) => k.startsWith('weather_'))
      .forEach((k) => localStorage.removeItem(k));
  }
};

export const clearWeatherCache = () => {
  Object.keys(localStorage)
    .filter((k) => k.startsWith('weather_'))
    .forEach((k) => localStorage.removeItem(k));
};

// ─── NWS fetch helper ────────────────────────────────────────────────────────

const nwsFetch = async (url) => {
  const response = await fetch(url, {
    headers: { Accept: 'application/geo+json' },
  });
  if (!response.ok) {
    throw new Error(`NWS API error: ${response.status}`);
  }
  return response.json();
};

// ─── NWS icon URL → condition code mapping ───────────────────────────────────

const nwsIconToConditionCode = (iconUrl) => {
  if (!iconUrl) return '03d';
  const isNight = iconUrl.includes('/night/');
  const suffix = isNight ? 'n' : 'd';
  const match = iconUrl.match(/\/icons\/land\/(?:day|night)\/([^,?/]+)/);
  const condition = match ? match[1] : '';

  const mapping = {
    skc: '01', few: '02', sct: '02', bkn: '03', ovc: '04',
    wind_skc: '01', wind_few: '02', wind_sct: '02', wind_bkn: '03', wind_ovc: '04',
    snow: '13', rain_snow: '13', rain_sleet: '13', snow_sleet: '13',
    fzra: '13', rain_fzra: '13', snow_fzra: '13', sleet: '13', blizzard: '13',
    rain: '10', rain_showers: '09', rain_showers_hi: '09',
    tsra: '11', tsra_sct: '11', tsra_hi: '11', tornado: '11', hurricane: '11',
    dust: '50', smoke: '50', haze: '50', fog: '50',
    hot: '01', cold: '01',
  };

  return (mapping[condition] || '03') + suffix;
};

// ─── Wind speed parsing ──────────────────────────────────────────────────────

const parseWindSpeed = (windStr) => {
  if (!windStr) return 0;
  const numbers = windStr.match(/\d+/g);
  if (!numbers || numbers.length === 0) return 0;
  return parseInt(numbers[numbers.length - 1], 10);
};

// ─── Rain timing logic ──────────────────────────────────────────────────────
// Uses the actual day/night values from NWS directly — no guessing.

const RAIN_THRESHOLD = 30;

const getRainTiming = (dayPop, nightPop) => {
  const dp = dayPop || 0;
  const np = nightPop || 0;
  const dayRain = dp >= RAIN_THRESHOLD;
  const nightRain = np >= RAIN_THRESHOLD;
  if (dayRain && nightRain) return 'all_day';
  if (dayRain) return 'day';
  if (nightRain) return 'night';
  return 'none';
};

// ─── Pair day/night forecast periods into daily objects ──────────────────────

const pairForecastPeriods = (periods) => {
  const days = [];
  let i = 0;

  // If first period is night, create partial day
  if (periods.length > 0 && !periods[0].isDaytime) {
    days.push({ dayPeriod: null, nightPeriod: periods[0] });
    i = 1;
  }

  while (i < periods.length) {
    const dayPeriod = periods[i];
    const nightPeriod = i + 1 < periods.length && !periods[i + 1].isDaytime ? periods[i + 1] : null;
    days.push({ dayPeriod, nightPeriod });
    i += nightPeriod ? 2 : 1;
  }

  return days;
};

const normalizeDailyForecast = (periods) => {
  const paired = pairForecastPeriods(periods);

  return paired.map(({ dayPeriod, nightPeriod }) => {
    const primary = dayPeriod || nightPeriod;
    const startTime = primary.startTime;
    const dateObj = new Date(startTime);
    dateObj.setHours(0, 0, 0, 0);
    const dt = Math.floor(dateObj.getTime() / 1000);

    const highTemp = dayPeriod ? dayPeriod.temperature : nightPeriod?.temperature;
    const lowTemp = nightPeriod ? nightPeriod.temperature : dayPeriod?.temperature;

    const dayPop = dayPeriod?.probabilityOfPrecipitation?.value ?? 0;
    const nightPop = nightPeriod?.probabilityOfPrecipitation?.value ?? 0;

    const dayWind = dayPeriod ? parseWindSpeed(dayPeriod.windSpeed) : 0;
    const nightWind = nightPeriod ? parseWindSpeed(nightPeriod.windSpeed) : 0;

    const iconUrl = dayPeriod?.icon || nightPeriod?.icon || '';
    const conditionCode = nwsIconToConditionCode(iconUrl);

    return {
      dt,
      startTime,
      temp: {
        max: highTemp,
        min: lowTemp,
        day: dayPeriod ? dayPeriod.temperature : highTemp,
        night: nightPeriod ? nightPeriod.temperature : lowTemp,
      },
      rain: {
        probability: Math.max(dayPop, nightPop),
        timing: getRainTiming(dayPop, nightPop),
        dayPop,
        nightPop,
        dayDescription: dayPeriod?.shortForecast || '',
        nightDescription: nightPeriod?.shortForecast || '',
      },
      wind: {
        speed: Math.max(dayWind, nightWind),
        direction: dayPeriod?.windDirection || nightPeriod?.windDirection || '',
      },
      icon: iconUrl,
      conditionCode,
      shortForecast: dayPeriod?.shortForecast || nightPeriod?.shortForecast || '',
      detailedForecast: dayPeriod?.detailedForecast || nightPeriod?.detailedForecast || '',
    };
  });
};

// ─── Normalize alerts ────────────────────────────────────────────────────────

const normalizeAlerts = (alertsData) => {
  if (!alertsData?.features?.length) return [];
  return alertsData.features.map((f) => ({
    event: f.properties.event,
    description: f.properties.description,
  }));
};

// ─── Get grid points (cached 24h) ───────────────────────────────────────────

const getGridPoints = async (lat, lng) => {
  const pointsCacheKey = getPointsCacheKey(lat, lng);
  let pointsData = getCachedData(pointsCacheKey, POINTS_CACHE_TTL);

  if (!pointsData) {
    const latStr = parseFloat(lat).toFixed(4);
    const lngStr = parseFloat(lng).toFixed(4);
    const response = await nwsFetch(`${NWS_BASE}/points/${latStr},${lngStr}`);
    pointsData = {
      forecastUrl: response.properties.forecast,
      forecastHourlyUrl: response.properties.forecastHourly,
    };
    setCachedData(pointsCacheKey, pointsData);
  }

  return pointsData;
};

// ─── Main: getWeatherData ────────────────────────────────────────────────────

export const getWeatherData = async (lat, lng) => {
  if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
    throw new Error('Invalid coordinates for weather data.');
  }

  const cacheKey = getCacheKey(lat, lng);
  const cached = getCachedData(cacheKey);
  if (cached) return cached;

  try {
    const { forecastUrl } = await getGridPoints(lat, lng);

    const latStr = parseFloat(lat).toFixed(4);
    const lngStr = parseFloat(lng).toFixed(4);

    const [forecastData, alertsData] = await Promise.all([
      nwsFetch(forecastUrl),
      nwsFetch(`${NWS_BASE}/alerts/active?point=${latStr},${lngStr}`),
    ]);

    const periods = forecastData.properties?.periods || [];
    const daily = normalizeDailyForecast(periods);
    const alerts = normalizeAlerts(alertsData);

    const result = { daily, alerts };
    setCachedData(cacheKey, result);
    return result;
  } catch (err) {
    throw new Error('Unable to fetch weather data. Please try again later.');
  }
};

// ─── Hourly forecast for detail view ─────────────────────────────────────────

export const getHourlyForecast = async (lat, lng) => {
  if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
    throw new Error('Invalid coordinates.');
  }

  const cacheKey = getHourlyCacheKey(lat, lng);
  const cached = getCachedData(cacheKey);
  if (cached) return cached;

  try {
    const { forecastHourlyUrl } = await getGridPoints(lat, lng);
    const data = await nwsFetch(forecastHourlyUrl);
    const periods = data.properties?.periods || [];

    const hourly = periods.map((p) => ({
      time: p.startTime,
      temp: p.temperature,
      rain: p.probabilityOfPrecipitation?.value ?? 0,
      shortForecast: p.shortForecast,
      icon: p.icon,
      windSpeed: p.windSpeed,
      windDirection: p.windDirection,
    }));

    setCachedData(cacheKey, hourly);
    return hourly;
  } catch (err) {
    throw new Error('Unable to fetch hourly forecast.');
  }
};

// Stub for backward compatibility (no longer needed with NWS)
export const getRateLimitInfo = () => ({ count: 0, remaining: Infinity, date: '' });
